import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import type {
	JiraSearchApiIssue,
	JiraSearchApiResponse,
} from '@atlassian/jira-servicedesk-linked-object-incidents-store/src/services/active-incidents/types.tsx';
import { getFieldNameForJql } from '@atlassian/jira-servicedesk-linked-object-incidents-store/src/services/active-incidents/utils.tsx';
import {
	CHANGES,
	INCIDENTS,
} from '@atlassian/jira-servicedesk-work-category/src/common/constants.tsx';
import {
	CHANGE_EVENT,
	INCIDENT_EVENT,
	type ChangeTimelineEvent,
	type IncidentTimelineEvent,
	type TimelineEventsServiceResponse,
} from '../../common/types/timeline-event.tsx';
import { handleTimelineNetworkError } from '../../common/utils/index.tsx';

const LOGGER_LOCATION = 'insight-object.object-timeline.fetchChangesAndIncidents';
export const SEARCH_URL = '/rest/api/3/search';

const getQuery = (affectedServicesJQLFieldName: string, serviceId: string): string =>
	`("Ticket category" = Changes OR "Ticket category" = Incidents) AND ${affectedServicesJQLFieldName} = "${serviceId}" ORDER BY created DESC`;

const transformIssueSearchResponse = (
	jiraApiResponse: JiraSearchApiResponse,
	affectedServicesFieldId: string,
	requestTypeFieldId: string,
): TimelineEventsServiceResponse => {
	const getChangeEvent = ({ id, key, fields }: JiraSearchApiIssue): ChangeTimelineEvent => ({
		type: CHANGE_EVENT,
		timestamp: Date.parse(fields.created),
		location: `/browse/${key}`,
		data: {
			id,
			statusCategoryId: fields.status.statusCategory.id,
			statusCategory: fields.status.statusCategory.name,
			issueSummary: fields.summary,
			issueStatus: fields.status.name,
			issueKey: key,
			issuePriorityUrl: fields.priority?.iconUrl,
			issuePriorityName: fields.priority?.name,
			issueTypeIconUrl: fields.issuetype.iconUrl,
		},
	});

	const getIncidentEvent = ({ id, key, fields }: JiraSearchApiIssue): IncidentTimelineEvent => ({
		type: INCIDENT_EVENT,
		timestamp: Date.parse(fields.created),
		location: `/browse/${key}`,
		data: {
			id,
			statusCategoryId: fields.status.statusCategory.id,
			statusCategory: fields.status.statusCategory.name,
			issueSummary: fields.summary,
			issueStatus: fields.status.name,
			issueKey: key,
			issuePriorityUrl: fields.priority?.iconUrl,
			issuePriorityName: fields.priority?.name,
			issueTypeIconUrl: fields.issuetype.iconUrl,
		},
	});

	if (jiraApiResponse.issues) {
		return {
			data: jiraApiResponse.issues
				.filter((issue: JiraSearchApiIssue) =>
					[CHANGES, INCIDENTS].includes(issue.fields[requestTypeFieldId]?.requestType?.practice),
				)
				.map((issue: JiraSearchApiIssue) =>
					issue.fields[requestTypeFieldId].requestType.practice === CHANGES
						? getChangeEvent(issue)
						: getIncidentEvent(issue),
				),
		};
	}

	return {
		data: [],
	};
};

export const fetchChangesAndIncidents = async (
	affectedServicesFieldId: string,
	requestTypeFieldId: string,
	serviceId: string,
	start = 0,
	maxResults = 50,
): Promise<TimelineEventsServiceResponse> => {
	try {
		const affectedServicesJQLAlias = getFieldNameForJql(affectedServicesFieldId);
		const response: JiraSearchApiResponse = await performPostRequest(SEARCH_URL, {
			body: JSON.stringify({
				jql: getQuery(affectedServicesJQLAlias, serviceId),
				expand: ['renderedFields', `${requestTypeFieldId}.requestTypePractice`],
				startAt: start,
				maxResults,
			}),
		});

		return transformIssueSearchResponse(response, affectedServicesFieldId, requestTypeFieldId);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		return handleTimelineNetworkError(error, LOGGER_LOCATION);
	}
};
